import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useQueryClient } from '@tanstack/react-query';
import {
  Container,
  Row,
  Col,
  PageHeader,
  Button,
  Text,
  Alert,
} from '@cochlear-design-system/foundation';
import { Feature } from 'flagged';
import { routes } from '../../config';
import { getLabelsForFields } from '../../utils/labelHelper';
import getQueryString from '../../utils/queryStrings';
import providerService from '../../services/providerService';
import DisabledFeature from '../DisabledFeature';
import MarkDeceasedModal from '../MarkDeceasedModal';
import headerCancelModal from '../../actions/headerCancelModalAction';
import { PersonalDetailsView } from './components/PersonalDetailsView';
import { ContactDetailsView } from './components/ContactDetailsView';
import { AddressView } from './components/AddressView';
import { getErrorConfig } from './editPatientConfig';
import error from '../../actions/errorAction';
import { usePatientDetails } from '../../hooks/usePatientDetails';
import googleMapsConfigService from '../../services/googleMapsConfigService';

const EditPatient = ({ rendering, country, lang }) => {
  const labels = getLabelsForFields(rendering.fields);
  const history = useHistory();
  const modalTrigger = useSelector(
    (payload) => payload.headerCancelModal,
  );

  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const currentProvider = providerService.getCurrentProvider();
  const providerId = currentProvider?.id;
  const providerCountryCode = currentProvider?.address?.countryCode;

  const patientId = getQueryString('patientId');
  const [carerId] = useState(getQueryString('carerId'));

  const location = useLocation();
  const state = useState(location.state)[0];

  const [headingTitle, setHeadingTitle] = useState(
    labels['labels.common.editPatient'],
  );

  const [showMarkDeceasedModal, setShowMarkDeceasedModal] =
    useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const [pageLevelError, setPageLevelError] = useState(false);
  const [googleMapsApiKey, setGoogleMapsApiKey] = useState(null);
  const config = getErrorConfig();

  // Use React Query to fetch patient/carer details
  const {
    data: patientDetails,
    isLoading,
    error: fetchError,
  } = usePatientDetails(providerId, patientId, carerId, {
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    retry: 1,
  });

  // Function to update patient details in React Query cache
  const updatePatientDetailsCache = (newData) => {
    const queryKey = carerId
      ? ['carer', providerId, patientId, carerId]
      : ['patient', providerId, patientId];

    queryClient.setQueryData(queryKey, newData);
  };

  // Fetch Google Maps API key on component mount
  useEffect(() => {
    const fetchGoogleMapsApiKey = async () => {
      const apiKey =
        await googleMapsConfigService.getGoogleMapsApiKey();
      setGoogleMapsApiKey(apiKey);
    };

    fetchGoogleMapsApiKey();
  }, []);

  // Handle fetch errors
  useEffect(() => {
    if (fetchError) {
      dispatch(
        error(
          fetchError.status === 404
            ? config.notFoundErrorConfig
            : true,
        ),
      );
      setPageLevelError(true);
    }
  }, [fetchError, dispatch, config.notFoundErrorConfig]);

  const getFullName = () => {
    return [
      patientDetails?.firstName || '',
      patientDetails?.lastName || '',
    ]
      .join(' ')
      .trim();
  };

  const handleCancel = () => {
    const optimisticCarerEnabled =
      localStorage.getItem('INSTANT') === 'true';

    let locationState;

    // Only pass optimistic carer if it was SAVED successfully
    // If user just cancels without saving, don't pass stale cache data
    if (
      optimisticCarerEnabled &&
      carerId &&
      saveSuccess &&
      patientDetails
    ) {
      const optimisticCarer = {
        data: {
          id: carerId,
          firstName: patientDetails.firstName,
          lastName: patientDetails.lastName,
          email: patientDetails.email || null,
          preferredLanguage: patientDetails.preferredLanguage || null,
          dateOfBirth: patientDetails.dateOfBirth || null,
          mobile: patientDetails.mobile || null,
          phone: patientDetails.phone || null,
          address: patientDetails.address || null,
          accountStatus: patientDetails.accountStatus || null,
        },
        errors: [],
      };
      locationState = { optimisticCarer };
      sessionStorage.setItem(
        `dpx_optimisticCarer_${patientId}`,
        JSON.stringify(optimisticCarer),
      );
    }
    // If just canceling (no save), don't pass any optimistic data
    // Let PatientRecord fetch fresh carer list instead

    history.push({
      pathname: `/${country}/${lang}/${routes.dpx.patientDetails}`,
      search: `?patientId=${patientId}`,
      state: locationState,
    });
  };

  const handlePatientMarkedDeceased = (success) => {
    setShowMarkDeceasedModal(false);
    if (success) {
      history.push(`/${country}/${lang}/${routes.dpx.home}`);
    }
  };

  useEffect(() => {
    dispatch(error(false));
  }, [dispatch]);

  // Update heading title when patient details are loaded
  useEffect(() => {
    if (patientDetails) {
      setHeadingTitle(getFullName());
    }
  }, [patientDetails]);

  useEffect(() => {
    if (modalTrigger) {
      dispatch(headerCancelModal(false));
      handleCancel();
    }
  }, [modalTrigger]);

  // NOTE: styles for below are hardcodedin main.scss
  return (
    !pageLevelError && (
      <Feature name="featureEnabler/viewPatient">
        {(isEnabled) =>
          isEnabled ? (
            <div className="ccl__edit-patient-wrapper">
              <Container>
                <Row>
                  <Col>
                    <PageHeader
                      className="edit-patient__title"
                      title={{ content: headingTitle }}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col>
                    {state?.addCarer && (
                      <Alert
                        variant="success"
                        dismissible
                        data-analytics="close_add_carer_alert"
                      >
                        {
                          labels[
                            'labels.editPatient.addCarer.success'
                          ]
                        }
                      </Alert>
                    )}
                    {saveSuccess && (
                      <Alert variant="success">
                        {carerId
                          ? labels[
                              'labels.editPatient.carer.update.success'
                            ]
                          : labels[
                              'labels.editPatient.patient.update.success'
                            ]}
                      </Alert>
                    )}
                    {saveError && (
                      <Alert variant="error">
                        {carerId
                          ? labels[
                              'labels.editPatient.carer.update.error'
                            ]
                          : labels[
                              'labels.editPatient.patient.update.error'
                            ]}
                      </Alert>
                    )}
                  </Col>
                </Row>
                <Row>
                  <Col xs={12}>
                    <div className="ccl-l-userTabs ccl-l-userTabs--single">
                      <div className="ccl-l-userTabs__content">
                        <div className="ccl-l-userTabs__content__inner">
                          <PersonalDetailsView
                            labels={labels}
                            providerId={providerId}
                            patientDetails={patientDetails}
                            setPatientDetails={
                              updatePatientDetailsCache
                            }
                            loading={isLoading}
                            setSaveSuccess={setSaveSuccess}
                            setSaveError={setSaveError}
                            carerFor={carerId ? patientId : null}
                          />

                          <ContactDetailsView
                            labels={labels}
                            providerId={providerId}
                            patientDetails={patientDetails}
                            setPatientDetails={
                              updatePatientDetailsCache
                            }
                            loading={isLoading}
                            setSaveSuccess={setSaveSuccess}
                            setSaveError={setSaveError}
                            carerFor={carerId ? patientId : null}
                          />

                          <AddressView
                            labels={labels}
                            providerId={providerId}
                            providerCountryCode={providerCountryCode}
                            patientDetails={patientDetails}
                            loading={isLoading}
                            googleMapsApiKey={googleMapsApiKey}
                            isCarer={!!carerId}
                          />
                        </div>
                      </div>
                    </div>
                  </Col>
                </Row>
                <Row>
                  <div className="d-flex flex-row justify-content-between pt-4">
                    <div className="pe-2">
                      <Button
                        text={labels['labels.common.close']}
                        variant="secondary"
                        onClick={handleCancel}
                        data-analytics={
                          carerId
                            ? 'close_edit_carer'
                            : 'close_edit_patient'
                        }
                      />
                    </div>
                    {!patientDetails?.isDeceased && (
                      <div className="pe-2">
                        <Text
                          content={
                            labels[
                              'labels.patientRecord.edit.markAsDeceased'
                            ]
                          }
                          type="link-small-text"
                          onClick={() =>
                            setShowMarkDeceasedModal(true)
                          }
                          data-analytics={
                            carerId
                              ? 'mark_deceased_edit_carer'
                              : 'mark_deceased_edit_patient'
                          }
                        />
                      </div>
                    )}
                  </div>
                </Row>
              </Container>
              <MarkDeceasedModal
                fields={rendering.fields}
                show={showMarkDeceasedModal}
                onHide={handlePatientMarkedDeceased}
                providerId={providerId}
                patientId={patientId}
                carerId={carerId}
              />
            </div>
          ) : (
            <DisabledFeature
              title={labels['labels.common.patientRecord']}
              message={
                labels['labels.common.featureFlag.disabledText']
              }
            />
          )
        }
      </Feature>
    )
  );
};

export default EditPatient;
